import {
  OFFER_PLAN,
  OFFER_SINGLE_OPTION_DISCOUNT_TYPE,
  OFFER_TRANSACTION,
  OfferMultipleOptionsAddonProduct,
  OfferMultipleOptionsCoreProduct,
  OfferSingleOptionProduct
} from '@ag1/atlas';
import { useMemo } from 'react';
import { PortalOffer } from '@portal/types/offer';
import { ShopifyProductVariant } from '@portal/api/shopifyV2/types';
import { getShopifyStringId } from '@portal/shared/utils/shopify/getShopifyStringId';
import { useLocaleForAtlas } from '@portal/shared/utils/offer/generic';
import { CustomContent, CustomOfferPricesItem, FieldOptionsWithRouter } from '../types';
import {
  getComplexContentObjectArray,
  getContentBlockField,
  getPlaceholderForPreview,
  shouldShowContentBlockPlaceholders
} from '../utils';

const { SingleOptionOffer } = OFFER_TRANSACTION;
const { FirstMonthFree } = OFFER_PLAN;
const { PerProduct } = OFFER_SINGLE_OPTION_DISCOUNT_TYPE;

interface Props {
  customContent: CustomContent[];
  routerField: FieldOptionsWithRouter;
  offer?: PortalOffer | null;
  shopifyVariantsInCurrentStore: ShopifyProductVariant[] | undefined;
  coreProducts?: OfferMultipleOptionsCoreProduct[] | null;
  addonProducts?: OfferMultipleOptionsAddonProduct[] | null;
  singleOptionProducts?: OfferSingleOptionProduct[] | null;
}

export const useContentBlockOfferPriceRowsData = ({
  customContent,
  routerField: { slug, router },
  offer,
  shopifyVariantsInCurrentStore = [],
  coreProducts,
  addonProducts,
  singleOptionProducts
}: Props): CustomOfferPricesItem[] => {
  const atlasLocale = useLocaleForAtlas() || 'us';

  return useMemo(() => {
    const transactionType = offer?.transaction_type;

    if (transactionType === SingleOptionOffer) {
      const offerLocaleConfig = offer?.single_option_product_selections_config?.[atlasLocale] ?? [];
      const applyRowDiscounts = offer?.single_option_discount_type?.[atlasLocale] === PerProduct;
      const offerLocaleDiscountConfig = offer?.single_option_discount_config?.[atlasLocale];

      const priceRowsData = offerLocaleConfig.reduce((list, config) => {
        const product = singleOptionProducts?.find(
          ({ id, product_data }) =>
            id === config?.product_id &&
            product_data.intended_usages.includes(config.intended_usage)
        );

        if (product) {
          const shopifyVariantId = getShopifyStringId(
            product.product_data?.shopify_product_variant_uri
          );

          const matchingVariantData = shopifyVariantsInCurrentStore.find((variant) =>
            variant.id.endsWith(shopifyVariantId || '')
          );

          if (matchingVariantData) {
            const quantity = product.quantity ?? 1;
            const rawPrice =
              matchingVariantData?.contextualPricing?.price?.amount ??
              matchingVariantData?.price ??
              '0';

            let discountType = null;
            let discountAmount = null;

            if (applyRowDiscounts && offerLocaleDiscountConfig) {
              const discountEntry = offerLocaleDiscountConfig.find(
                (x) => x.product_type_id === config.product_type_id
              );
              if (discountEntry) {
                discountType = discountEntry.type;
                discountAmount = discountEntry.amount;
              }
            }

            list.push({
              name: matchingVariantData?.product.title || '[Product Loading]',
              description:
                config.intended_usage === 'subscription'
                  ? `${
                      Number(matchingVariantData?.product.servingsPerContainer?.value || 1) *
                      quantity
                    } Servings`
                  : matchingVariantData.product.description?.value || '',
              price: Number(rawPrice) * quantity,
              quantity,
              shopifyVariantId,
              intendedUsage: config.intended_usage,
              isFirstMonthFree: false,
              discountType,
              discountAmount
            });
          }
        }
        return list;
      }, [] as CustomOfferPricesItem[]);

      if (priceRowsData.length > 0) {
        return priceRowsData;
      }
    } else {
      const offerLocaleConfig =
        offer?.multiple_options_core_product_selections_config?.[atlasLocale] ?? [];

      const firstCoreProductConfig = offerLocaleConfig[0];

      const firstCoreProduct = coreProducts?.find(
        ({ id }) => id === firstCoreProductConfig?.product_id
      );

      if (firstCoreProduct) {
        const shopifyVariantId = getShopifyStringId(
          firstCoreProduct.product_data?.shopify_product_variant_uri
        );

        const matchingVariantData = shopifyVariantsInCurrentStore.find((variant) =>
          variant.id.endsWith(shopifyVariantId || '')
        );

        const quantity = firstCoreProduct.quantity ?? 1;
        const rawPrice =
          matchingVariantData?.contextualPricing?.price?.amount ??
          matchingVariantData?.price ??
          '0';

        const firstCoreProductPriceRow: CustomOfferPricesItem = {
          name: matchingVariantData?.product.title || '[Product Loading]',
          description: `${
            Number(matchingVariantData?.product.servingsPerContainer?.value || 1) * quantity
          } Servings`,
          price: Number(rawPrice) * quantity,
          quantity,
          shopifyVariantId,
          intendedUsage: firstCoreProduct.product_data.intended_usages[0],
          isFirstMonthFree: offer?.type === FirstMonthFree,
          discountType: null,
          discountAmount: null
        };

        const addonProductsPriceRows = firstCoreProductConfig.addons.reduce((list, addon) => {
          const addonProduct = addonProducts?.find(({ id }) => id === addon.product_id);

          if (addonProduct) {
            const addonVariantId = getShopifyStringId(
              addonProduct?.product_data.shopify_product_variant_uri ?? ''
            );

            const addonVariantData = shopifyVariantsInCurrentStore.find(({ id }) =>
              id.endsWith(addonVariantId || '')
            );

            const addonQuantity = addonProduct?.quantity ?? 1;

            const unitPrice =
              Number(addonVariantData?.contextualPricing?.price?.amount) ??
              Number(addonVariantData?.price ?? 0);

            const price = unitPrice * addonQuantity;

            list.push({
              name: addon.name || addonVariantData?.product?.title || '[Add-on Loading]',
              description: addonVariantData?.product?.description?.value || '',
              price,
              quantity: addonQuantity,
              shopifyVariantId: addonVariantId,
              intendedUsage: 'addon',
              isFirstMonthFree: false,
              discountType: addon.discount_type,
              discountAmount: addon.discount_amount
            });
          }

          return list;
        }, [] as CustomOfferPricesItem[]);

        return [firstCoreProductPriceRow, ...addonProductsPriceRows];
      }
    }

    // preview fallback when nothing is selected
    const content =
      getComplexContentObjectArray<CustomOfferPricesItem>(
        getContentBlockField(customContent, 'offer-prices', { slug })
      ) || [];

    if (!content.length && shouldShowContentBlockPlaceholders(router)) {
      content.push(
        // "Primary" items are shown on top.
        {
          name: 'Primary Product',
          description: 'This is a description of the primary product.',
          price: 79,
          intendedUsage: 'subscription',
          isFirstMonthFree: false,
          discountType: null,
          discountAmount: null
        } satisfies CustomOfferPricesItem,
        // These are shown below the primary item.
        {
          name: 'Add-on Product',
          description: 'This is included with your purchase at no extra cost.',
          price: 19,
          intendedUsage: 'addon',
          isFirstMonthFree: false,
          discountType: null,
          discountAmount: null
        } satisfies CustomOfferPricesItem
      );
    }

    return content.reduce((acc, i, index) => {
      if (i.hide) return acc;

      acc.push({
        name: i.text || getPlaceholderForPreview(router, `Text ${index + 1}`, ''),
        description: i.description || getPlaceholderForPreview(router, 'Description', ''),
        price: i.price || 0,
        quantity: i.quantity || 1,
        shopifyVariantId: i.shopifyVariantId,
        intendedUsage: i.intendedUsage || 'subscription',
        isFirstMonthFree: false,
        discountType: null,
        discountAmount: null
      });

      return acc;
    }, [] as CustomOfferPricesItem[]);
  }, [
    offer,
    atlasLocale,
    coreProducts,
    customContent,
    slug,
    router,
    shopifyVariantsInCurrentStore,
    addonProducts,
    singleOptionProducts
  ]);
};
